import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Button, Typography, Alert } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { BRAND_COLORS } from '../styles/theme';
import { useTranslation, LanguageSwitcher } from '../i18n';
import XERLogo from '../components/XERLogo';

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Auto-redirect after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/forgot-password');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
      }}
    >
      {/* Language Switcher - top right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher variant="compact" />
      </Box>

      <Paper
        elevation={24}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          mx: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <XERLogo width={120} />
          <Typography
            variant="h5"
            sx={{ mt: 2, fontWeight: 600, color: BRAND_COLORS.forestGreen.main }}
          >
            {t('passwordReset.processUpdatedTitle')}
          </Typography>
        </Box>

        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          {t('passwordReset.processUpdatedMessage')}
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('passwordReset.instructionsTitle')}
        </Typography>

        <Box component="ol" sx={{ mb: 3, pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('passwordReset.instruction1')}
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('passwordReset.instruction2')}
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('passwordReset.instruction3')}
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            {t('passwordReset.instruction4')}
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate('/forgot-password')}
          sx={{
            mb: 1,
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: '#002B20',
            },
          }}
        >
          {t('passwordReset.goToPasswordReset')}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{
            borderColor: BRAND_COLORS.forestGreen.main,
            color: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              borderColor: BRAND_COLORS.forestGreen.main,
              backgroundColor: `${BRAND_COLORS.forestGreen.main}10`,
            },
          }}
        >
          {t('auth.backToLogin')}
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, textAlign: 'center', display: 'block' }}
        >
          {t('passwordReset.autoRedirect')}
        </Typography>
      </Paper>
    </Box>
  );
};
